import { forwardRef, useId, ComponentPropsWithoutRef, useRef } from "react";
import cx from "classnames";
import { motion } from "motion/react";

import { DataTrackingId, LayoutUtilProps, Size } from "../../types";
import { useMergeRefs, useTrackingId } from "../../hooks";
import { useOptionallyControlledState } from "../../internal/hooks";

import { SegmentedControlContext } from "./internal/SegmentedControlContext";
import {
  SegmentedControlSegment,
  SegmentedControlSegmentProps,
} from "./SegmentedControlSegment";

import styles from "./SegmentedControl.module.scss";

/**
 * Base props for SegmentedControl component
 * @extends Omit<ComponentPropsWithoutRef<"div">, "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag" | "onChange">
 * @extends LayoutUtilProps
 */
type BaseSegmentedControlProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag" | "onChange"
> &
  LayoutUtilProps &
  DataTrackingId & {
    /**
     * Callback when segment is changed
     */
    onChange?: (value: SegmentedControlSegmentProps["value"]) => void;

    /**
     * Size of the segment
     * @default "medium"
     */
    size?: Extract<Size, "small" | "medium">;

    /**
     * Whether to fill available width
     * @default false
     */
    fill?: boolean;

    /**
     * Name attribute for radio inputs
     * @remarks If empty, it will auto generate one using useId()
     */
    name?: string;

    /**
     * The segments to render within the segmented control
     */
    children?: React.ReactNode;
  };

/**
 * Props for controlled SegmentedControl
 */
type ControlledSegmentedControlProps = BaseSegmentedControlProps & {
  /**
   * Selected segment (controlled mode)
   */
  selected: SegmentedControlSegmentProps["value"];
  defaultSelected?: never;
};

/**
 * Props for uncontrolled SegmentedControl
 */
type UncontrolledSegmentedControlProps = BaseSegmentedControlProps & {
  /**
   * Initial selection (uncontrolled mode)
   */
  defaultSelected: SegmentedControlSegmentProps["value"];
  selected?: never;
};

/**
 * Props for the SegmentedControl component
 */
export type SegmentedControlProps =
  | ControlledSegmentedControlProps
  | UncontrolledSegmentedControlProps;

/**
 * SegmentedControl component for selecting one option from a group of related choices.
 *
 * Features:
 * - Supports both controlled and uncontrolled modes
 * - Multiple size variants (small, medium)
 * - Optional fill width mode
 * - Smooth animations with motion
 * - Accessibility support with proper radio group semantics
 * - Layout utility props for positioning and spacing
 * - Context-based state management
 * - Auto-generated or custom name attributes
 * - Segment support via SegmentedControl.Segment
 * - Data tracking id support
 *
 * @example
 * <SegmentedControl
 *   selected={selectedValue}
 *   onChange={setSelectedValue}
 *   size="medium"
 * >
 *   <SegmentedControl.Segment value="option1">Option 1</SegmentedControl.Segment>
 *   <SegmentedControl.Segment value="option2">Option 2</SegmentedControl.Segment>
 *   <SegmentedControl.Segment value="option3">Option 3</SegmentedControl.Segment>
 * </SegmentedControl>
 *
 * @example
 * <SegmentedControl
 *   defaultSelected="small"
 *   size="small"
 *   fill
 * >
 *   <SegmentedControl.Segment value="small" icon={SmallIcon}>Small</SegmentedControl.Segment>
 *   <SegmentedControl.Segment value="large" icon={LargeIcon}>Large</SegmentedControl.Segment>
 * </SegmentedControl>
 */
export const SegmentedControl = Object.assign(
  forwardRef<HTMLDivElement, SegmentedControlProps>(
    function SegmentedControlInner(props, ref) {
      const {
        className,
        onClick,
        onChange,
        children,
        size = "medium",
        defaultSelected,
        selected,
        name,
        fill,
        ...rest
      } = props;

      const [current, setCurrent] = useOptionallyControlledState({
        controlledValue: selected,
        defaultValue: defaultSelected,
        onChange,
      });

      const segmentedControlRef = useRef<HTMLDivElement>(null);

      const SegmentedControlClassNames = cx(
        styles["segmented-control"],
        styles[size],
        className,
        {
          [styles.fill]: fill,
        },
      );

      const uid = useId();

      const data = {
        name: props.name,
      };

      const trackingId = useTrackingId({
        name: "SegmentedControl",
        data,
        hasOverride: !!props["data-tracking-id"],
      });

      return (
        <motion.div
          transition={{ duration: 0 }}
          layout
          role="radiogroup"
          className={SegmentedControlClassNames}
          data-anv="segmented-control"
          data-tracking-id={trackingId}
          ref={useMergeRefs([segmentedControlRef, ref])}
          {...rest}
        >
          <SegmentedControlContext.Provider
            value={{ current, setCurrent, name: name ?? uid }}
          >
            {children}
          </SegmentedControlContext.Provider>
        </motion.div>
      );
    },
  ),
  {
    /**
     * SegmentedControlSegment component for individual segments within a segmented control.
     *
     * Features:
     * - Individual segment with radio input
     * - Optional icon support
     * - Animated selection indicator
     * - Accessibility support with proper ARIA attributes
     * - Context integration with parent SegmentedControl
     * - Motion animations with reduced motion support
     *
     * @example
     * <SegmentedControl.Segment value="option1" icon={Icon1}>
     *   Option 1
     * </SegmentedControl.Segment>
     */
    Segment: SegmentedControlSegment,
  },
);
SegmentedControl.displayName = "SegmentedControl";

export default SegmentedControl;
